import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import config from 'config/config';
import { setGlobalMessage } from 'containers/MessageHandler/actions';
import Analytics from 'utils/Analytics';
import checkOrSetSlash from 'utils/checkOrSetSlash';
import { prepareFCHeader } from 'utils/request';

const baseUrl = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/`;

const transformStatuses = statuses => {
  const statusesCopy = [...statuses];
  return statuses.map(status => {
    // on récupère les status qui sont dans "statuses" et qui ne sont pas dans la propriété "to_statuses" de status
    const missingStatuses = statusesCopy.filter(
      statusToCheck =>
        statusToCheck.id !== status.id &&
        !status.to_statuses.some(toStatus => toStatus.id === statusToCheck.id),
    );

    // si on a pas de status manquant, on renvoie le status tel quel
    if (!missingStatuses.length) return status;

    // sinon on ajoute les status manquants dans la propriété "to_statuses" de status
    return {
      ...status,
      to_statuses: [
        ...status.to_statuses,
        ...missingStatuses.map(statusToCheck => ({
          id: statusToCheck.id,
          is_deleted: !!statusToCheck.deleted_at,
          project_role_ids: [],
        })),
      ],
    };
  });
};

const handleQueryStarted = async (queryFulfilled, dispatch, analyticsKey) => {
  try {
    const { data } = await queryFulfilled;
    if (data && !data.message) {
      dispatch(
        setGlobalMessage({
          type: 'success',
          intlMessage: {
            id: 'library_update_published',
          },
          active: true,
        }),
      );
      Analytics.track(analyticsKey);
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour des statuts :', error);
  }
};

export const statusesApi = createApi({
  reducerPath: 'statusesApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: prepareFCHeader(),
  }),
  tagTypes: ['ProjectStatusesList', 'OrganizationStatusesList', 'WorkspaceStatusesList'],
  endpoints: builder => ({
    fetchProjectStatuses: builder.query({
      query: ({ projectId, limit = 1000, offset = 0 }) => ({
        url: `v1.4/projects/${projectId}/statuses`,
        params: {
          limit,
          offset,
          with_workflow: true,
          without_disabled: true,
        },
        method: 'GET',
      }),
      transformResponse: response => ({
        ...response,
        statuses: transformStatuses(response.statuses),
      }),
      providesTags: ['ProjectStatusesList'],
    }),
    fetchOrganizationStatuses: builder.query({
      query: ({ organizationId }) => ({
        url: `v1.1/businessOrganization/${organizationId}/module/observation/status`,
        method: 'GET',
      }),
      transformResponse: response => ({
        ...response,
        values: transformStatuses(response.values),
      }),
      providesTags: ['OrganizationStatusesList'],
    }),
    fetchWorkspaceStatuses: builder.query({
      query: ({ organizationId, workspaceId }) => ({
        url: `v1.1/businessOrganization/${organizationId}/workspaces/${workspaceId}/module/observation/status`,
        method: 'GET',
      }),
      transformResponse: response => ({
        ...response,
        values: transformStatuses(response.values),
      }),
      providesTags: ['WorkspaceStatusesList'],
    }),
    fetchSuperAdminStatuses: builder.query({
      query: () => ({
        url: 'v1.1/module/observation/status',
        method: 'GET',
      }),
    }),
    updateProjectStatuses: builder.mutation({
      query: ({ projectId, statuses }) => ({
        url: `v1.4/projects/${projectId}/statuses`,
        method: 'PUT',
        body: { statuses },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleQueryStarted(
          queryFulfilled,
          dispatch,
          'librairies_project_status_workflow_updated',
        );
      },
      invalidatesTags: ['ProjectStatusesList'],
    }),
    updateOrganizationStatuses: builder.mutation({
      query: ({ organizationId, statuses, updateOnlyNewProjects, projectIds }) => ({
        url: `v1.1/businessOrganization/${organizationId}/module/observation/status`,
        method: 'PUT',
        body: {
          statuses,
          push_on_existing_projects: !updateOnlyNewProjects,
          project_ids: projectIds,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleQueryStarted(queryFulfilled, dispatch, 'librairies_status_workflow_updated');
      },
      invalidatesTags: ['OrganizationStatusesList'],
    }),
    updateWorkspaceStatuses: builder.mutation({
      query: ({ organizationId, workspaceId, statuses, updateOnlyNewProjects, projectIds }) => ({
        url: `v1.1/businessOrganization/${organizationId}/workspaces/${workspaceId}/module/observation/statuses`,
        method: 'PUT',
        body: {
          statuses,
          push_on_existing_projects: !updateOnlyNewProjects,
          project_ids: projectIds,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleQueryStarted(queryFulfilled, dispatch, 'librairies_status_workflow_updated');
      },
      invalidatesTags: ['WorkspaceStatusesList'],
    }),
    updateSuperAdminStatuses: builder.mutation({
      query: ({ statuses }) => ({
        url: `v1.1/module/observation/status`,
        method: 'PUT',
        body: {
          statuses,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleQueryStarted(queryFulfilled, dispatch, 'librairies_status_workflow_updated');
      },
      invalidatesTags: ['OrganizationStatusesList'],
    }),
  }),
});

export const {
  useFetchProjectStatusesQuery,
  useFetchOrganizationStatusesQuery,
  useFetchWorkspaceStatusesQuery,
  useFetchSuperAdminStatusesQuery,
  useUpdateProjectStatusesMutation,
  useUpdateOrganizationStatusesMutation,
  useUpdateWorkspaceStatusesMutation,
  useUpdateSuperAdminStatusesMutation,
} = statusesApi;
